import axios from 'axios';

const BASE_URL = import.meta.env?.VITE_API_URL || 'https://yourdomain.com/api';

const api = axios?.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Request interceptor — attach JWT token
api?.interceptors?.request?.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle errors globally
api?.interceptors?.response?.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const isNetwork = !error?.response && error?.request;
    const isTimeout = error?.code === 'ECONNABORTED';

    // 401 — session expired
    if (status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      if (!window.location?.pathname?.includes('/login')) {
        // Dispatch event — AuthContext listens and calls logout(),
        // then ProtectedRoute redirects to /login client-side (no server 404)
        window.dispatchEvent(new CustomEvent('api:session-expired'));
      }
    }

    // 500+ server errors — log in dev
    if (status >= 500) {
      if (import.meta.env?.DEV) {
        console.error('[API Server Error]', status, error?.response?.data);
      }
      window.dispatchEvent(new CustomEvent('api:server-error', { detail: { status, message: error?.response?.data?.message } }));
    }

    // Network / offline errors
    if (isNetwork || isTimeout) {
      window.dispatchEvent(new CustomEvent('api:network-error', { detail: { isTimeout } }));
    }

    // Normalize error shape for consumers
    const normalized = {
      status: status || null,
      message: isTimeout
        ? 'Request timed out. Please try again.'
        : isNetwork
        ? 'No internet connection. Check your connection and try again.'
        : error?.response?.data?.message || error?.response?.data?.error || error?.message || 'An unexpected error occurred.',
      errors: error?.response?.data?.errors || [],
      type: isNetwork || isTimeout ? 'network' : status === 401 ? 'unauthorized' : status === 422 ? 'validation' : status >= 500 ? 'server' : 'unknown',
    };
    error._normalized = normalized;

    return Promise.reject(error);
  }
);

export default api;

// ── Plans (public) ────────────────────────────────────────────────────────────
export const plansApi = {
  getAll: () => api?.get('/plans'),
};

// ── Auth ────────────────────────────────────────────────────────────────────
export const authApi = {
  login:            (data) => api?.post('/auth/login', data),
  register:         (data) => api?.post('/auth/register', data),
  logout:           ()     => api?.post('/auth/logout'),
  refresh:          (data) => api?.post('/auth/refresh', data),
  forgotPassword:   (data) => api?.post('/auth/forgot-password', data),
  resetPassword:    (data) => api?.post('/auth/reset-password', data),
  changePassword:   (data) => api?.post('/auth/change-password', data),
  getMe:            ()     => api?.get('/auth/me'),
  updateProfile:    (data) => api?.put('/auth/profile', data),
  verifyEmail:          (data) => api?.post('/auth/verify-email', data),
  resendVerification:   (data) => api?.post('/auth/resend-verification', data),
  unsubscribe:          (data) => api?.post('/auth/unsubscribe', data),
  resubscribe:          ()     => api?.post('/auth/resubscribe'),
  // 2FA
  setup2FA:             ()     => api?.post('/auth/2fa/setup'),
  enable2FA:            (data) => api?.post('/auth/2fa/enable', data),
  disable2FA:           (data) => api?.post('/auth/2fa/disable', data),
  verify2FA:            (data) => api?.post('/auth/2fa/verify', data),
  // Security
  getSessions:          ()     => api?.get('/auth/sessions'),
  revokeSession:        (id)   => api?.delete(`/auth/sessions/${id}`),
  getApiKey:            ()     => api?.get('/auth/api-key'),
  regenerateApiKey:     ()     => api?.post('/auth/api-key'),
  deleteData:           ()     => api?.delete('/auth/delete-data'),
  deleteAccount:        ()     => api?.delete('/auth/delete-account'),
};

// ── Portfolios ─────────────────────────────────────────────────────────────
export const portfoliosApi = {
  getAll:   ()         => api?.get('/portfolios'),
  create:   (data)     => api?.post('/portfolios', data),
  update:   (id, data) => api?.put(`/portfolios/${id}`, data),
  delete:   (id)       => api?.delete(`/portfolios/${id}`),
};

// ── Holdings ─────────────────────────────────────────────────────────────────
export const holdingsApi = {
  getAll:   (portfolioId, status = 'all') => api?.get('/holdings', { params: { portfolio_id: portfolioId, status } }),
  getSummary:(portfolioId)=> api?.get('/holdings/summary', { params: { portfolio_id: portfolioId } }),
  create:   (data)        => api?.post('/holdings', data),
  update:   (id, data)    => api?.put(`/holdings/${id}`, data),
  delete:   (id)          => api?.delete(`/holdings/${id}`),
  sell:     (id, data)    => api?.post(`/holdings/${id}/sell`, data),
  merge:    (keepId, mergeId) => api?.post('/holdings/merge', { keep_id: keepId, merge_id: mergeId }),
};

// ── Dividends ────────────────────────────────────────────────────────────────
export const dividendsApi = {
  getAll:    (params)     => api?.get('/dividends', { params }),
  getCalendar:(params)    => api?.get('/dividends/calendar', { params }),
  create:    (data)       => api?.post('/dividends', data),
  update:    (id, data)   => api?.put(`/dividends/${id}`, data),
  delete:    (id)         => api?.delete(`/dividends/${id}`),
};

// ── Options ───────────────────────────────────────────────────────────────────
export const optionsApi = {
  getAll:    (params)          => api?.get('/options', { params }),
  getSummary:(portfolioId)     => api?.get('/options/summary', { params: { portfolio_id: portfolioId } }),
  create:    (data)            => api?.post('/options', data),
  update:    (id, data)        => api?.put(`/options/${id}`, data),
  delete:    (id)              => api?.delete(`/options/${id}`),
  importCsv:       (formData)  => api?.post('/options/import-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getMonthlyStats: (year)      => api?.get('/options/monthly-stats', { params: { year } }),
};

// ── Reports ───────────────────────────────────────────────────────────────────
export const reportsApi = {
  getIncome:            (params) => api?.get('/reports/income', { params }),
  getMonthlyTrend:      (params) => api?.get('/reports/monthly-trend', { params }),
  getTaxHarvesting:     ()       => api?.get('/reports/tax-harvesting'),
  getDividendSchedule:  (params) => api?.get('/reports/dividend-schedule', { params }),
  getAnnualComparison:  ()       => api?.get('/reports/annual-comparison'),
};

// ── Settings ──────────────────────────────────────────────────────────────────
export const settingsApi = {
  getAll:       ()     => api?.get('/settings'),
  updateAll:    (data) => api?.put('/settings', data),
  getEmail:     ()     => api?.get('/settings/email'),
  updateEmail:  (data) => api?.put('/settings/email', data),
};

// ── Admin ─────────────────────────────────────────────────────────────────────
export const adminApi = {
  getUsers:         (params)   => api?.get('/admin/users', { params }),
  getPlanChanges:   (params)   => api?.get('/admin/plan-changes', { params }),
  getRevenue:       (params)   => api?.get('/admin/revenue', { params }),
  updateUser:       (id, data) => api?.put(`/admin/users/${id}`, data),
  getStats:         ()         => api?.get('/admin/stats'),
  sendEmail:        (data)     => api?.post('/admin/email/send', data),
  getEmailLogs:     (params)   => api?.get('/admin/email/logs', { params }),
  getDeliveryStats: ()         => api?.get('/admin/email/delivery-stats'),
  getBounces:       (params)   => api?.get('/admin/email/bounces', { params }),
  getUnsubscribes:  (params)   => api?.get('/admin/email/unsubscribes', { params }),
  getRetryQueue:    ()         => api?.get('/admin/email/retry-queue'),
  retryEmail:       (data)     => api?.post('/admin/email/retry', data),
  sendWeeklyDigest: (data)     => api?.post('/admin/email/send-weekly-digest', data ?? {}),
  getEmailTemplates:()              => api?.get('/admin/email/templates'),
  getTemplate:      (id)           => api?.get(`/admin/email/templates/${id}`),
  updateTemplate:   (id, data)     => api?.put(`/admin/email/templates/${id}`, data),
  resetTemplate:    (id)           => api?.delete(`/admin/email/templates/${id}`),
  // Plans management
  getPlans:         ()         => api?.get('/admin/plans'),
  updatePlan:       (id, data) => api?.put(`/admin/plans/${id}`, data),
};

// ── FMP ───────────────────────────────────────────────────────────────────────
export const fmpApi = {
  getUpcomingDividends: (days = 30, portfolioId = null) => api?.get('/fmp/dividends/upcoming', { params: portfolioId ? { days, portfolio_id: portfolioId } : { days } }),
  syncDividendHistory:  ()          => api?.post('/fmp/dividends/sync'),
  // Full enrichment: updates price + dividend_per_share + dividend_yield + frequency + company/sector
  updatePrices:         (portfolioId = null) => api?.post('/fmp/holdings/refresh', portfolioId ? { portfolio_id: portfolioId } : {}),
  /** GET /fmp/stock-metrics?tickers=AAPL,JNJ — dividend growth + safety score */
  getStockMetrics:      (tickers = []) => api?.get('/fmp/stock-metrics', { params: { tickers: tickers.join(',') } }),
};

// ── SnapTrade ─────────────────────────────────────────────────────────────────
// ── Alerts ────────────────────────────────────────────────────────────────────
export const alertsApi = {
  getAll:     ()           => api?.get('/alerts'),
  create:     (data)       => api?.post('/alerts', data),
  update:     (id, data)   => api?.put(`/alerts/${id}`, data),
  delete:     (id)         => api?.delete(`/alerts/${id}`),
  test:       (id)         => api?.post(`/alerts/${id}/test`),
  // Run the full monitor, or pass types=['options_profit_target'] for an instant targeted check
  runMonitor: (types)      => api?.post('/alerts/monitor/run', null, {
    params: types?.length ? { types: types.join(',') } : {},
  }),
};

export const snaptradeApi = {
  sync:             ()       => api?.post('/snaptrade/sync'),  // pull actual transactions from brokerage
  debugActivities:  (params) => api?.get('/snaptrade/debug-activities', { params }),
  debugPositions:   (params) => api?.get('/snaptrade/debug-positions',   { params }),
  debugLogs:        (params) => api?.get('/snaptrade/sync-debug-logs', { params }),
  clearDebugLogs:   ()       => api?.delete('/snaptrade/sync-debug-logs'),
  get:              (path, config) => api?.get(path, config),
};

// ── Blog ─────────────────────────────────────────────────────────────────────
export const blogApi = {
  // Public (no auth)
  getPosts:    (params) => api.get('/blog', { params }),
  getPost:     (slug)   => api.get(`/blog/${slug}`),
  recordView:  (slug)   => api.post(`/blog/${slug}/view`),
  // Admin only
  getAllPosts:  ()       => api.get('/blog/admin/all'),
  createPost:  (data)   => api.post('/blog/admin', data),
  updatePost:  (id, d)  => api.put(`/blog/admin/${id}`, d),
  deletePost:  (id)     => api.delete(`/blog/admin/${id}`),
  uploadImage: (form)   => api.post('/blog/admin/upload-image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// ── AI Analyst ───────────────────────────────────────────────────────────────
export const aiApi = {
  analyze: () => api?.post('/ai/analyze', {}, { timeout: 60000 }),
  chat:    (messages) => api?.post('/ai/chat', { messages }, { timeout: 30000 }),
};

// ── News ─────────────────────────────────────────────────────────────────────
export const newsApi = {
  /** GET /news?filter={filter}  — filter: 'all' | 'holdings' */
  getFeed: (filter = 'all') => api?.get('/news', { params: { filter } }),
};

// ── Stripe ───────────────────────────────────────────────────────────────────
export const stripeApi = {
  createCheckout:  (data) => api?.post('/stripe/create-checkout', data),
  cancelSubscription: ()  => api?.post('/stripe/cancel'),
  getBillingHistory:  ()  => api?.get('/stripe/billing-history'),
};

// ── Affiliates ────────────────────────────────────────────────────────────────
export const affiliateApi = {
  // Affiliate-facing
  apply:          (data)       => api?.post('/affiliates/apply', data),
  getStatus:      ()           => api?.get('/affiliates/status'),
  getMe:          ()           => api?.get('/affiliates/me'),
  updateSettings: (data)       => api?.put('/affiliates/me', data),
  getConversions: (page = 1)   => api?.get(`/affiliates/conversions?page=${page}`),
  getPayouts:     ()           => api?.get('/affiliates/payouts'),
  requestPayout:  ()           => api?.post('/affiliates/payout-request'),
  // Admin
  adminGetStats:        ()            => api?.get('/affiliates/admin/stats'),
  adminGetSettings:     ()            => api?.get('/affiliates/admin/settings'),
  adminUpdateSettings:  (data)        => api?.put('/affiliates/admin/settings', data),
  adminGetList:         (p = {})      => api?.get('/affiliates/admin/list', { params: p }),
  adminApprove:         (id, data)    => api?.post(`/affiliates/admin/${id}/approve`, data),
  adminReject:          (id, data)    => api?.post(`/affiliates/admin/${id}/reject`, data),
  adminSuspend:         (id, data)    => api?.post(`/affiliates/admin/${id}/suspend`, data),
  adminGetConversions:  (p = {})      => api?.get('/affiliates/admin/conversions', { params: p }),
  adminApproveConv:     (id)          => api?.post(`/affiliates/admin/conversions/${id}/approve`),
  adminGetPayouts:      (status)      => api?.get(`/affiliates/admin/payouts?status=${status || 'pending'}`),
  adminProcessPayout:   (id, data)    => api?.post(`/affiliates/admin/payouts/${id}/process`, data),
};

// ── Ideas ─────────────────────────────────────────────────────────────────────
export const ideasApi = {
  /** Fetch the stock list for a given category slug.
   *  GET /ideas/{slug}  →  { data: StockRow[] }
   */
  getCategory: (slug) => api?.get(`/ideas/${slug}`),
};

// ── Promo codes ───────────────────────────────────────────────────────────────
export const promoApi = {
  redeem: (code) => api?.post('/promo/redeem', { code }),
};

// ── Watchlist ─────────────────────────────────────────────────────────────────
export const watchlistApi = {
  /** GET /watchlist — full list with live FMP data */
  getList:     ()             => api?.get('/watchlist'),
  /** GET /watchlist/tickers — lightweight array of ticker strings */
  getTickers:  ()             => api?.get('/watchlist/tickers'),
  /** POST /watchlist — add { ticker, notes?, target_price?, target_yield? } */
  add:         (data)         => api?.post('/watchlist', data),
  /** DELETE /watchlist/{ticker} */
  remove:      (ticker)       => api?.delete(`/watchlist/${ticker}`),
  /** PATCH /watchlist/{ticker} — update notes / targets */
  update:      (ticker, data) => api?.patch(`/watchlist/${ticker}`, data),
};
